<template>
  <banners>
    <div
      class="container-max relative mx-auto box-border min-h-screen w-full px-0 md:px-10"
    >
      <div class="mb-7 mt-10 flex items-center px-4">
        <div class="flex w-full flex-row items-center gap-40">
          <div
            ref="topElement"
            class="flex w-full items-center justify-between"
          >
            <h2 class="whitespace-nowrap text-3xl text-dark-gray">
              Rate Confirmations
            </h2>

            <div class="flex items-center gap-4">
              <form
                v-if="screenSize !== 'mobile'"
                class="relative flex items-center"
                @submit.prevent="search"
              >
                <input
                  v-model="searchText"
                  type="text"
                  class="h-9 w-full rounded-md border-placeholder-gray bg-lighter-gray bg-opacity-30 pl-8 pr-3 text-sm text-gray-500 shadow-sm outline-none"
                  placeholder="Search"
                />
                <button
                  type="submit"
                  class="absolute bottom-0 left-2 top-0 flex w-8 flex-col justify-center bg-transparent text-placeholder-gray hover:text-yellow-500"
                >
                  <img
                    class="h-4 w-4 cursor-pointer"
                    src="@/assets/search-light-gray.svg"
                    alt="search icon"
                  />
                </button>

                <img
                  v-if="searchText?.length > 0"
                  class="absolute right-4 h-4 w-4 cursor-pointer"
                  src="@/assets/close-icon-gray.svg"
                  alt="cancel"
                  @click="clearSearch"
                />
              </form>

              <span
                v-if="canRateCon && canViewSettings"
                class="hidden cursor-pointer rounded-lg bg-gray-100 px-4 py-2 hover:bg-hovered-gray md:block"
                @click="handleOpenRateConSettingsModal"
                >Settings</span
              >
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!canRateCon"
        class="flex items-center justify-center"
      >
        <div
          class="mx-8 mt-4 flex h-fit w-[600px] w-full gap-2 rounded-lg bg-yellow-10 p-4 text-start lg:mx-4"
        >
          <img
            class="mt-1 h-6 w-6"
            src="@/assets/yellow-question-mark.svg"
            alt="question mark"
          />

          <div class="flex flex-col gap-2">
            <p class="text-lg">You don’t have access to rate confirmations</p>
            <p class="text-sm text-neutral-subtle">
              Please contact your company's system administrator to request
              access.
            </p>
          </div>
        </div>
      </div>

      <div
        v-else
        class="rounded-md lg:p-4"
      >
        <form
          v-if="screenSize === 'mobile'"
          class="relative mb-4 ml-4 mr-6 flex items-center gap-2"
          @submit.prevent="search"
        >
          <input
            v-model="searchText"
            type="text"
            class="h-9 w-full rounded-md border-placeholder-gray bg-lighter-gray bg-opacity-30 pl-8 pr-3 text-sm text-gray-500 shadow-sm outline-none"
            placeholder="Search"
          />
          <button
            type="submit"
            class="absolute bottom-0 left-2 top-0 flex w-8 flex-col justify-center bg-transparent text-placeholder-gray hover:text-yellow-500"
          >
            <img
              class="h-4 w-4 cursor-pointer"
              src="@/assets/search-light-gray.svg"
              alt="search icon"
            />
          </button>

          <img
            v-if="searchText?.length > 0"
            class="absolute right-10 h-4 w-4 cursor-pointer"
            src="@/assets/close-icon-gray.svg"
            alt="cancel"
            @click="clearSearch"
          />

          <img
            v-if="canViewSettings"
            class="h-5 w-5 cursor-pointer"
            src="@/assets/filter.svg"
            alt="filter icon"
            @click="handleOpenRateConSettingsModal"
          />
        </form>

        <div class="container-max b-1 w-full px-4 lg:px-0">
          <div class="flex items-center gap-3 overflow-scroll lg:gap-6">
            <div
              v-for="tab in pageTabs"
              :key="`${tab}-tab-link`"
              class="cursor-pointer whitespace-nowrap border-b-2 text-sm font-medium md:text-lg"
              :class="{
                'border-transparent text-neutral-subtlest hover:border-neutral-subtlest':
                  activePageTab !== tab,
                'border-yellow-500 text-yellow-500': activePageTab == tab,
              }"
              @click="setActivePageTab(tab)"
            >
              {{ tab }}
              <span v-if="tabCount(tab) !== undefined"
                >({{ tabCount(tab) }})</span
              >
            </div>
          </div>
        </div>

        <div class="mt-4 min-h-screen bg-gray-100 md:bg-white">
          <div
            v-if="isLoading"
            class="flex h-96 justify-center bg-off-white"
          >
            <div
              class="inner-container flex flex-col items-start justify-center p-8 py-16"
            >
              <div class="mx-auto h-16 w-16 text-center">
                <img
                  class="-top-2px loading relative w-full"
                  src="@/assets/loading-v2-yellow.svg"
                  alt=""
                />
              </div>
              <div class="mt-4 text-dark-gray">
                <h3 class="text-center text-xl">Loading Rate Confirmations</h3>
              </div>
            </div>
          </div>
          <div v-else>
            <div
              v-if="!hasAnyRates"
              class="flex items-center justify-center"
            >
              <div
                class="mx-8 mt-4 flex h-fit w-full max-w-[600px] gap-2 rounded-lg bg-yellow-10 p-4 text-start lg:mx-4"
              >
                <img
                  class="mt-1 h-6 w-6"
                  src="@/assets/yellow-question-mark.svg"
                  alt="question mark"
                />

                <div class="flex flex-col gap-2">
                  <p
                    v-if="isBrokerUser"
                    class="text-lg"
                  >
                    Protect your rate confirmations from bad actors
                  </p>
                  <p
                    v-else
                    class="text-lg"
                  >
                    Looks like you don't have any
                    {{
                      activePageTab !== 'All'
                        ? activePageTab?.toLowerCase()
                        : ''
                    }}
                    rate confirmations
                  </p>
                  <p
                    v-if="isBrokerUser"
                    class="text-sm text-neutral-subtle"
                  >
                    Secure Rate Confirmation Delivery prevents sending sensitive
                    load info via email, stopping unauthorized access and cargo
                    theft.
                  </p>
                  <p
                    v-else
                    class="text-sm text-neutral-subtle"
                  >
                    Protect your rate confirmations from bad actors by
                    encouraging brokers to adopt
                    <b>Secure Rate Confirmation Delivery</b>, an optional
                    feature that allows a broker to email any rate con to your
                    DOT number: dot{{ carrierDotNumber }}@highway.com
                  </p>

                  <button-main
                    variant="tertiary"
                    class="mt-2 !w-fit bg-gray-100 !px-3 !py-2 !text-black"
                    @click="handleRateConLearnMore"
                  >
                    Learn More
                  </button-main>
                </div>
              </div>
            </div>
            <div v-else>
              <div
                v-if="rates?.length > 0 && activePageTab == 'All'"
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="rates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="ratesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="parseInt(ratesPagination.currentPage)"
                    :total-pages="parseInt(ratesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(ratesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>
              <div
                v-else-if="viewedRates?.length > 0 && activePageTab == 'Viewed'"
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="viewedRates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="viewedRatesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="parseInt(viewedRatesPagination.currentPage)"
                    :total-pages="parseInt(viewedRatesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(viewedRatesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>
              <div
                v-else-if="deliveredRates?.length > 0 && activePageTab == 'New'"
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="deliveredRates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="deliveredRatesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="
                      parseInt(deliveredRatesPagination.currentPage)
                    "
                    :total-pages="parseInt(deliveredRatesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(deliveredRatesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>
              <div
                v-else-if="
                  acceptedRates?.length > 0 && activePageTab == 'Accepted'
                "
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="acceptedRates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="acceptedRatesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="
                      parseInt(acceptedRatesPagination.currentPage)
                    "
                    :total-pages="parseInt(acceptedRatesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(acceptedRatesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>
              <div
                v-else-if="
                  cancelledRates?.length > 0 && activePageTab == 'Cancelled'
                "
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="cancelledRates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="acceptedRatesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="
                      parseInt(cancelledRatesPagination.currentPage)
                    "
                    :total-pages="parseInt(cancelledRatesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(cancelledRatesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>

              <div
                v-else-if="
                  rejectedRates?.length > 0 && activePageTab == 'Rejected'
                "
                class="pb-14"
              >
                <rate-confirmation-table
                  :rate-data="rejectedRates"
                  :search-text="searchText"
                  :screen-size="screenSize"
                  @sort="sort"
                  @accept-terms="openTermsModal"
                />
                <div
                  v-if="acceptedRatesPagination.totalPages > 1"
                  class="mt-2 flex justify-center p-2"
                >
                  <pagination
                    :selected-page="
                      parseInt(rejectedRatesPagination.currentPage)
                    "
                    :total-pages="parseInt(rejectedRatesPagination.totalPages)"
                    :pages-around="
                      counterPageAround(rejectedRatesPagination.totalPages)
                    "
                    equivalent
                    @select-page="onSelectPage"
                  />
                </div>
              </div>

              <div
                v-else
                class="flex items-center justify-center"
              >
                <div
                  class="mx-8 mt-4 flex h-fit w-full max-w-[600px] gap-2 rounded-lg bg-yellow-10 p-4 text-start lg:mx-4"
                >
                  <img
                    class="mt-1 h-6 w-6"
                    src="@/assets/yellow-question-mark.svg"
                    alt="question mark"
                  />

                  <div class="flex flex-col gap-2">
                    <p
                      v-if="isBrokerUser"
                      class="text-lg"
                    >
                      Protect your rate confirmations from bad actors
                    </p>
                    <p
                      v-else
                      class="text-lg"
                    >
                      Looks like you don't have any
                      {{
                        activePageTab !== 'All'
                          ? activePageTab?.toLowerCase()
                          : ''
                      }}
                      rate confirmations
                    </p>
                    <p
                      v-if="isBrokerUser"
                      class="text-sm text-neutral-subtle"
                    >
                      Secure Rate Confirmation Delivery prevents sending
                      sensitive load info via email, stopping unauthorized
                      access and cargo theft.
                    </p>
                    <p
                      v-else
                      class="text-sm text-neutral-subtle"
                    >
                      Protect your rate confirmations from bad actors by
                      encouraging brokers to adopt
                      <b>Secure Rate Confirmation Delivery</b>, an optional
                      feature that allows a broker to email any rate con to your
                      DOT number: dot{{ carrierDotNumber }}@highway.com
                    </p>

                    <button-main
                      variant="tertiary"
                      class="mt-2 !w-fit bg-gray-100 !px-3 !py-2 !text-black"
                      @click="handleRateConLearnMore"
                    >
                      Learn More
                    </button-main>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <terms-modal
      v-if="showTermsModal"
      :visible="showTermsModal"
      :terms="brokerTerms"
      :terms-rate="termsRate"
      @close="onCloseTermsModal"
    />

    <rate-con-settings-modal
      v-if="showRateConSettingsModal"
      :visible="showRateConSettingsModal"
      :emails="rateConfirmationEmails"
      @close="handleCloseRateConSettingsModal"
    >
    </rate-con-settings-modal>
  </banners>
</template>

<script>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';
import debounce from 'lodash.debounce';
import Banners from '@/components/Banners.vue';
import Pagination from '@/components/Pagination.vue';
import RateConfirmationTable from '@/components/carrier/RateConfirmationTable.vue';
import TermsModal from '@/components/modals/TermsModal.vue';
import RateConSettingsModal from '@/components/modals/RateConSettingsModal.vue';
import ButtonMain from '@/components/ButtonMain.vue';

const TableColumnKey = {
  companyName: 'legal_name',
};

const SortDirection = {
  asc: 'asc',
  dsc: 'desc',
};

export default {
  name: 'RateConfirmationIndex',
  components: {
    Banners,
    RateConfirmationTable,
    Pagination,
    TermsModal,
    RateConSettingsModal,
    ButtonMain,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const rates = computed(() => [...store.state.rates.rates]);
    const viewedRates = computed(() => [...store.state.rates.viewed_rates]);
    const deliveredRates = computed(() => [
      ...store.state.rates.delivered_rates,
    ]);
    const acceptedRates = computed(() => [...store.state.rates.accepted_rates]);
    const cancelledRates = computed(() => [
      ...store.state.rates.cancelled_rates,
    ]);
    const rejectedRates = computed(() => [...store.state.rates.rejected_rates]);
    const brokerTerms = computed(() => store.state.rates.current_rate_terms);
    const canRateCon = computed(() =>
      store.state.users?.currentUser?.scopes?.includes(
        'view_rate_confirmations'
      )
    );

    const hasAnyRates = computed(() => {
      if (deliveredRates.value?.length > 0 || rates.value?.length > 0) {
        return true;
      }
      return false;
    });

    const isBrokerUser = computed(
      () => store.state.users?.currentUser?.company?.company_type === 'broker'
    );

    const carrierDotNumber = computed(
      () =>
        store.state.carriers?.carrierDetails?.identifiers?.find(
          (id) => id.is_type === 'DOT'
        )?.value_non_padded
    );

    const handleRateConLearnMore = () => {
      if (isBrokerUser.value) {
        window.open(
          'https://help.highway.com/en/articles/10910720-secure-rate-confirmation-delivery',
          '_blank'
        );
      } else {
        window.open(
          'https://service.highway.com/en/articles/11060695-secure-rate-confirmation-delivery',
          '_blank'
        );
      }
      return;
    };

    const screenSize = ref('desktop');

    const windowWidth = ref(window.innerWidth);

    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    // Watch for changes in window width
    watch(windowWidth, () => {
      if (window.innerWidth >= 768) {
        screenSize.value = 'desktop';
      } else {
        screenSize.value = 'mobile';
      }
    });

    onMounted(async () => {
      isLoading.value = true;
      // if (canRateCon.value) {
      const getEmailsQuery = `?q[is_type_eq]=rate_confirmation&q[carrier_id_eq]=${store.state.users.currentUser.company.carrier_id}`;

      try {
        await store.dispatch('rates/getDeliveredRates', {
          currentPage: 1,
        });

        if (store.state.rates?.delivered_rates_is_loaded) {
          isLoading.value = false;
        }
        await Promise.all([
          store.dispatch('rates/getViewedRates', { currentPage: 1 }),
          store.dispatch('rates/getAcceptedRates', { currentPage: 1 }),
          store.dispatch('rates/getCancelledRates', { currentPage: 1 }),
          store.dispatch('rates/getRejectedRates', { currentPage: 1 }),
          store.dispatch('rates/getAllRates', { currentPage: 1 }),
          store.dispatch('emails/getEmails', { query: getEmailsQuery }),
          store.dispatch('onboardingPacket/getLatestCompletedOnboardingPacket'),
          // store.dispatch("rates/getRateTerms"),
        ]);
      } catch (error) {
        console.log('error fetching rates', error);
      } finally {
        isLoading.value = false;
      }
      // }

      window.addEventListener('resize', updateWidth);
      // set initial screen size
      if (window.innerWidth >= 768) {
        screenSize.value = 'desktop';
      } else {
        screenSize.value = 'mobile';
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateWidth);
    });

    let pageTabs = ref([
      'New',
      'Viewed',
      'Accepted',
      'Cancelled',
      'Rejected',
      'All',
    ]);
    const searchText = ref('');
    const topElement = ref(null);

    let activePageTab = ref('New');
    const currentSortDir = ref(SortDirection.dsc);
    const currentSort = ref(TableColumnKey.companyName);

    const ratesPagination = computed(() => store.state.rates?.rates_pagination);
    const viewedRatesPagination = computed(
      () => store.state.rates?.viewed_rates_pagination
    );
    const deliveredRatesPagination = computed(
      () => store.state.rates?.delivered_rates_pagination
    );
    const acceptedRatesPagination = computed(
      () => store.state.rates?.accepted_rates_pagination
    );
    const cancelledRatesPagination = computed(
      () => store.state.rates?.cancelled_rates_pagination
    );
    const rejectedRatesPagination = computed(
      () => store.state.rates?.rejected_rates_pagination
    );

    const isLoading = ref(false);
    const termsRate = ref(null);

    const paginationMap = {
      All: () => ratesPagination.value?.totalRecords,
      Viewed: () => viewedRatesPagination.value?.totalRecords,
      New: () => deliveredRatesPagination.value?.totalRecords,
      Cancelled: () => cancelledRatesPagination.value?.totalRecords,
      Accepted: () => acceptedRatesPagination.value?.totalRecords,
      Rejected: () => rejectedRatesPagination.value?.totalRecords,
    };

    const tabCount = (tab) => {
      const getTotal = paginationMap[tab];
      const total = getTotal ? getTotal() : 0;
      return total > 100 ? '100+' : total;
    };

    const counterPageAround = (pages) => {
      const NumberOfPages = parseInt(pages);
      if (NumberOfPages > 5) {
        return 2;
      } else {
        return NumberOfPages;
      }
    };

    const onSelectPage = async (newPage) => {
      isLoading.value = true;
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      if (activePageTab.value === 'All') {
        await store.dispatch('rates/getAllRates', { currentPage: newPage });
      }
      if (activePageTab.value === 'Viewed') {
        await store.dispatch('rates/getViewedRates', { currentPage: newPage });
      }
      if (activePageTab.value === 'New') {
        await store.dispatch('rates/getDeliveredRates', {
          currentPage: newPage,
        });
      }
      isLoading.value = false;
    };

    const onSearchClick = async () => {
      // isLoading.value = true;
      // await store.dispatch("carriers/getCarriersByCompanyId", {
      //   companyId: userCompanyId,
      //   page: 1,
      //   sortBy: currentSort.value,
      //   sortDir: currentSortDir.value,
      //   searchText: searchText.value
      // });
      // isLoading.value = false;
    };

    const sort = (s) => {
      if (s === currentSort.value) {
        currentSortDir.value =
          currentSortDir.value === SortDirection.asc
            ? SortDirection.dsc
            : SortDirection.asc;
        return;
      }
      currentSort.value = s;
    };

    const setActivePageTab = (tab) => {
      // searchText.value = '';
      activePageTab.value = tab;
    };

    watch(rates, (newVal) => {
      if (newVal?.length > 0) {
        store.dispatch('rates/getRateTerms', rates.value?.[0]?.id);
      }
    });

    const onRowClick = (rate) => {
      // store.dispatch('carriers/getRateDetail', rate?.id);

      router.push({
        name: 'Rate Confirmation',
        params: { id: rate?.id },
      });
    };

    const showTermsModal = ref(false);

    const openTermsModal = (rate) => {
      showTermsModal.value = true;
      termsRate.value = rate;
    };

    const onCloseTermsModal = (rate) => {
      if (rate?.id > 0) {
        onRowClick(termsRate.value);
      } else {
        termsRate.value = null;
      }
      showTermsModal.value = false;
    };

    const showRateConSettingsModal = ref(false);

    const canViewSettings = computed(() =>
      store.state.users.currentUser?.scopes?.includes('manage_users')
    );

    const handleOpenRateConSettingsModal = () => {
      showRateConSettingsModal.value = true;
    };

    const handleCloseRateConSettingsModal = () => {
      showRateConSettingsModal.value = false;
    };

    const rateConfirmationEmails = computed(() =>
      store.state.emails.emails?.filter((email) => email.is_active)
    );

    const userRateConfirmationsEnabled = ref(false);

    const getRates = async (user_id = '') => {
      isLoading.value = true;
      await Promise.all([
        store.dispatch('rates/getDeliveredRates', { user_id }),
        store.dispatch('rates/getViewedRates', { user_id }),
        store.dispatch('rates/getAcceptedRates', { user_id }),
        store.dispatch('rates/getAllRates', { user_id }),
      ]);

      isLoading.value = false;
    };

    watch(userRateConfirmationsEnabled, (newValue) => {
      const user_id = store.state.users.currentUser.id;
      if (newValue) {
        getRates(user_id);
      } else {
        getRates();
      }
    });

    const searchRates = debounce(async (text) => {
      isLoading.value = true;

      const rateSearchText = isBrokerUser?.value
        ? `&q[carrier_legal_name_or_subject_cont]=${text}`
        : `&q[issuer_legal_name_or_subject_cont]=${text}`;

      try {
        await Promise.all([
          await store.dispatch('rates/getDeliveredRates', {
            searchText: rateSearchText,
          }),
          await store.dispatch('rates/getViewedRates', {
            searchText: rateSearchText,
          }),
          await store.dispatch('rates/getAcceptedRates', {
            searchText: rateSearchText,
          }),
          await store.dispatch('rates/getAllRates', {
            searchText: rateSearchText,
          }),
        ]);
      } catch (error) {
        console.error('error searching rate confirmation: ', error);
      } finally {
        isLoading.value = false;
      }
    }, 500);

    const clearSearch = () => {
      isLoading.value = true;
      searchText.value = '';

      searchRates('');
    };

    watch(searchText, (newVal) => {
      searchRates(newVal);
    });

    return {
      // canRateCon,
      rates,
      viewedRates,
      isBrokerUser,
      carrierDotNumber,
      userRateConfirmationsEnabled,
      deliveredRates,
      acceptedRates,
      cancelledRates,
      ratesPagination,
      viewedRatesPagination,
      deliveredRatesPagination,
      acceptedRatesPagination,
      cancelledRatesPagination,
      TableColumnKey,
      sort,
      onSelectPage,
      onSearchClick,
      isLoading,
      topElement,
      searchText,
      pageTabs,
      activePageTab,
      tabCount,
      setActivePageTab,
      counterPageAround,
      brokerTerms,
      onRowClick,
      showTermsModal,
      openTermsModal,
      onCloseTermsModal,
      termsRate,
      canRateCon,
      screenSize,
      canViewSettings,
      showRateConSettingsModal,
      handleOpenRateConSettingsModal,
      handleCloseRateConSettingsModal,
      rateConfirmationEmails,
      handleRateConLearnMore,
      clearSearch,
      hasAnyRates,
      rejectedRates,
      rejectedRatesPagination,
    };
  },
};
</script>
